import { CoilType, Command, CommandMan, ValveCommand, type Coil, type ModuleValve } from "@eisox/api/core-api";
import { ActionButton, Circle, Modal } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { HeatIcon, HydraulicChangeoverIcon, PencilIcon, SnowFlackIcon, ZapIcon } from "@eisox/icons";
import { getDirtyValues } from "@eisox/tools";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import CoolingUnit from "~/assets/coolingUnit.svg?react";
import { Mode } from "~/components/live-history-toggle";
import { m } from "~/paraglide/messages";

import { useUpdateAirHandlerUnit } from "../hooks/use-update-air-handler-unit";
import { useAirHandlerUnitContext } from "../providers/air-handler-unit.provider";
import * as ActuatorDialog from "./actuator-dialog";

const NAME = "CoilDialog";

const COIL_TITLE: Record<CoilType, () => string> = {
  [CoilType.HYDRAULIC_HEATING]: m.air_handler_unit_coil_hydraulic_heating,
  [CoilType.HYDRAULIC_COOLING]: m.air_handler_unit_coil_hydraulic_cooling,
  [CoilType.ELECTRIC]: m.air_handler_unit_coil_electric,
  [CoilType.HYDRAULIC_CHANGEOVER]: m.air_handler_unit_coil_hydraulic_changeover,
  [CoilType.CHILLER]: m.air_handler_unit_coil_chiller,
};

const COIL_ICON: Record<CoilType, React.ReactElement> = {
  [CoilType.HYDRAULIC_HEATING]: <HeatIcon />,
  [CoilType.HYDRAULIC_COOLING]: <SnowFlackIcon />,
  [CoilType.ELECTRIC]: <ZapIcon />,
  [CoilType.HYDRAULIC_CHANGEOVER]: <HydraulicChangeoverIcon />,
  [CoilType.CHILLER]: (
    <Circle.Root backgroundColor="darkBlue" color="white">
      <CoolingUnit />
    </Circle.Root>
  ),
};

/** Valve commands are inverted vs the other actuators (`ValveCommand.AUTO = 1`, `MANUAL = 2`). */
const VALVE_COMMAND_OPTIONS = [
  { value: ValveCommand.AUTO, name: m.actuator_state({ state: "auto" }) },
  { value: ValveCommand.MANUAL, name: m.actuator_state({ state: "forcing" }) },
];

interface ValveFormValues {
  id: string;
  command: number | undefined;
  forcing: number | undefined;
}

interface CoilFormValues {
  id: string;
  command: number | undefined;
  forcing: number | undefined;
  twoWayValve: ValveFormValues | undefined;
  threeWayValve: ValveFormValues | undefined;
}

const valveFormValues = (valve: ModuleValve | undefined): ValveFormValues | undefined =>
  valve ? { id: valve.id, command: valve.command, forcing: valve.forcing } : undefined;

interface CoilDialogProps extends Modal.RootProps {
  buildingId: string;
  airHandlerUnitId: string;
  coil: Coil;
  children?: React.ReactNode;
}

const CoilDialog: React.FC<CoilDialogProps> = ({
  buildingId,
  airHandlerUnitId,
  coil,
  open: openProp,
  onOpenChange,
  defaultOpen,
  children,
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen ?? false,
  });

  const { mode } = useAirHandlerUnitContext(NAME);
  const disabled = mode === Mode.History;

  const { mutateAsync } = useUpdateAirHandlerUnit(buildingId);

  const defaultValues: CoilFormValues = {
    id: coil.id,
    command: coil.command,
    forcing: coil.forcing,
    twoWayValve: valveFormValues(coil.twoWayValve),
    threeWayValve: valveFormValues(coil.threeWayValve),
  };
  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value, formApi }) => {
      if (disabled) return;
      const dirty = getDirtyValues(formApi.options.defaultValues, value, { identity: "id" });
      if (!dirty) return;
      await mutateAsync({ airHandlerUnitId, body: { coils: [dirty] } });
      setOpen(false);
    },
  });

  // The form outlives the modal content, so reopening must restore the coil's current values.
  const handleOpenChange = (next: boolean) => {
    if (next) form.reset();
    setOpen(next);
  };

  const renderValveGroup = (
    label: string,
    valve: ModuleValve,
    fieldName: "twoWayValve" | "threeWayValve",
  ): React.ReactElement => (
    <ActuatorDialog.Group label={label}>
      {valve.command !== undefined && (
        <form.AppField name={`${fieldName}.command`}>
          {commandField => (
            <ActuatorDialog.Item label={m.control()}>
              <ActuatorDialog.Command
                commandMan={coil.commandMan}
                value={commandField.state.value}
                onValueChange={value => commandField.handleChange(value)}
                options={VALVE_COMMAND_OPTIONS}
                disabled={disabled}
              />
              <form.AppField name={`${fieldName}.forcing`}>
                {forcingField => (
                  <ActuatorDialog.Forcing
                    command={commandField.state.value === ValveCommand.MANUAL ? Command.MANUAL : Command.AUTO}
                    commandMan={coil.commandMan}
                    commandType={coil.commandType}
                    value={forcingField.state.value}
                    onValueChange={value => forcingField.handleChange(value)}
                    disabled={disabled}
                  />
                )}
              </form.AppField>
            </ActuatorDialog.Item>
          )}
        </form.AppField>
      )}
      <ActuatorDialog.Item label={m.state()}>
        <ActuatorDialog.State value={`${valve.opening ?? "-"}%`} />
      </ActuatorDialog.Item>
    </ActuatorDialog.Group>
  );

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange}>
      {children && <Modal.Trigger asChild>{children}</Modal.Trigger>}
      <Modal.Content>
        <form
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <Modal.Header
              title={COIL_TITLE[coil.coilType]()}
              subtitle={m.air_handler_unit_actuator_dialog_subtitle()}
              icon={COIL_ICON[coil.coilType]}
            >
              <Modal.Close asChild>
                <ActionButton variant="cancel">{m.cancel()}</ActionButton>
              </Modal.Close>
              {!disabled && <form.SubmitButton label={m.save()} icon={<PencilIcon />} />}
            </Modal.Header>
            <ActuatorDialog.Group label={m.air_handler_unit_coil()}>
              {(coil.command !== undefined || coil.commandMan === CommandMan.MANUAL) && (
                <form.AppField name="command">
                  {commandField => (
                    <ActuatorDialog.Item label={m.control()}>
                      <ActuatorDialog.Command
                        commandMan={coil.commandMan}
                        value={commandField.state.value}
                        onValueChange={value => commandField.handleChange(value)}
                        disabled={disabled}
                      />
                      <form.AppField name="forcing">
                        {forcingField => (
                          <ActuatorDialog.Forcing
                            command={commandField.state.value}
                            commandMan={coil.commandMan}
                            commandType={coil.commandType}
                            value={forcingField.state.value}
                            onValueChange={value => forcingField.handleChange(value)}
                            disabled={disabled}
                          />
                        )}
                      </form.AppField>
                    </ActuatorDialog.Item>
                  )}
                </form.AppField>
              )}
              <ActuatorDialog.Item label={m.state()}>
                <ActuatorDialog.ActuatorState
                  commandType={coil.commandType}
                  state={coil.state}
                  commandAutomate={coil.commandAutomate}
                  commandMan={coil.commandMan}
                  defect={!!coil.defect}
                  missmatchDefect={!!coil.missmatchDefect}
                />
              </ActuatorDialog.Item>
              {coil.operationHours !== undefined && (
                <ActuatorDialog.Item label={m.air_handler_unit_actuator_operating_hours()}>
                  <ActuatorDialog.State value={`${coil.operationHours}h`} />
                </ActuatorDialog.Item>
              )}
            </ActuatorDialog.Group>
            {coil.twoWayValve && renderValveGroup(m.two_way_valve(), coil.twoWayValve, "twoWayValve")}
            {coil.threeWayValve && renderValveGroup(m.three_way_valve(), coil.threeWayValve, "threeWayValve")}
          </form.AppForm>
        </form>
      </Modal.Content>
    </Modal.Root>
  );
};

CoilDialog.displayName = NAME;

export { CoilDialog };
export type { CoilDialogProps };
